<script setup lang="ts">
  /**
   * Checkbox — 03-components/20-checkbox · OSCloud DS 1.1.151
   */
  import { onMounted, ref, watch } from 'vue'

  const props = withDefaults(
    defineProps<{
      modelValue?: boolean
      disabled?: boolean
      /** Partial-selection state (e.g. a "select all" that covers only some rows). */
      indeterminate?: boolean
      /** Neutral message under the control. */
      hint?: string
      /** Validation message, rendered under the control like BfInput's. */
      error?: string
    }>(),
    {
      modelValue: false,
      disabled: false,
      indeterminate: false,
      hint: '',
      error: '',
    },
  )

  defineEmits<{ 'update:modelValue': [value: boolean] }>()

  // Forward fall-through attrs (e.g. aria-label, id) to the input rather than the label,
  // so a checkbox with no slotted text still gets an accessible name.
  defineOptions({ inheritAttrs: false })

  // `indeterminate` is a DOM property, not an attribute, so it must be set on the element.
  const inputRef = ref<HTMLInputElement | null>(null)

  const syncIndeterminate = () => {
    if (inputRef.value) inputRef.value.indeterminate = props.indeterminate
  }

  onMounted(syncIndeterminate)
  watch(() => props.indeterminate, syncIndeterminate)
</script>

<template>
  <!-- The message is a sibling of the label, not inside it: nesting it would make
       it part of the checkbox's accessible name and toggle the box when clicked. -->
  <div class="form-field">
    <!-- No state modifiers: checked, disabled, invalid and indeterminate are all read off
         the input itself, so the class and the DOM property can never disagree. -->
    <label class="checkbox">
      <input
        ref="inputRef"
        type="checkbox"
        :checked="modelValue"
        :disabled="disabled"
        :aria-invalid="error ? 'true' : undefined"
        :data-indeterminate="indeterminate || undefined"
        v-bind="$attrs"
        @change="$emit('update:modelValue', ($event.target as HTMLInputElement).checked)"
      />
      <span class="checkbox__box">
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="square"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      <span class="form-field__label form-field__label--plain"><slot /></span>
    </label>
    <span
      v-if="error || hint"
      class="form-field__message"
      :role="error ? 'alert' : undefined"
      :data-test-id="error ? 'field-error' : undefined"
    >
      {{ error || hint }}
    </span>
  </div>
</template>

<style scoped lang="scss">
  .form-field {
    display: inline-flex;
    flex-direction: column;
    min-width: 0;

    &__label {
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      color: var(--bf-off-black);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
    }

    /* No `> .form-field__label` gap rule: the label sits inline with its control, not above
       it, so there is no label→control relationship for it to own. */
    &__label--plain {
      font-size: var(--bf-fs-xs);
      font-weight: var(--bf-fw-semibold);
      line-height: var(--bf-lh-normal);
      color: var(--bf-fg-1);
      text-transform: none;
      letter-spacing: var(--bf-tr-normal);
    }

    &__message {
      color: var(--bf-fg-2);
      font-size: var(--bf-fs-xs);
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: var(--bf-form-message-gap);
    }

    &__message[role='alert'],
    &:has([aria-invalid='true']) &__message {
      color: var(--bf-form-invalid-text);
    }
  }

  .checkbox {
    display: inline-flex;
    align-items: flex-start;
    gap: var(--bf-sp-2);
    cursor: pointer;
    user-select: none;

    input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    .form-field__label--plain {
      font-weight: var(--bf-fw-regular);
    }

    &__box {
      width: 16px;
      height: 16px;
      margin-top: 1px;
      border-radius: 4px;
      border: 1.5px solid var(--bf-dusky-2);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: var(--bf-form-control-surface);
      transition: all var(--bf-dur-fast) var(--bf-ease);
      flex-shrink: 0;

      svg {
        opacity: 0;
        color: var(--bf-white);
        transition: opacity var(--bf-dur-fast) var(--bf-ease);
      }
    }

    input:checked + &__box {
      background: var(--bf-orange-dark);
      border-color: var(--bf-orange-dark);

      svg {
        opacity: 1;
      }
    }

    &:has(input:indeterminate) &__box,
    &:has(input[data-indeterminate='true']) &__box {
      background: var(--bf-orange-dark);
      border-color: var(--bf-orange-dark);

      svg {
        display: none;
      }

      &::before {
        content: '';
        width: 9px;
        height: 2.5px;
        border-radius: 2px;
        background: var(--bf-white);
      }
    }

    input:focus-visible + &__box {
      border-color: var(--bf-form-control-hairline-focus);
      box-shadow: var(--bf-form-focus-ring);
    }

    &:has(input[aria-invalid='true']) &__box {
      border-color: var(--bf-form-invalid-hairline);
    }

    &:has(input:disabled) {
      opacity: var(--bf-form-disabled-opacity);
      cursor: not-allowed;
    }
  }
</style>
